import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';

import { Input } from '@/components/ui/inputs/input';
import { verifyTokenAuthService, forgetPasswordAuthService } from '@/services/AuthServices';
import validationErrors from '@/lib/validationErrors';

import backgroundImage from '@/assets/images/image1.jpg';
import logoImage from '@/assets/images/logo.png';

const BRAND = { primary: '#156372', yellow: '#FFC107', ink: '#0F2F35' };
const DEBUG = true;

const safeLS = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const safeSetLS = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

export default function VerifyEmailPage() {
  const { userData, isAuthenticated } = useSelector((s) => s?.Auth || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { control, handleSubmit, formState: { errors } } = useForm();
  const [isLoading, setIsLoading] = useState(false);

  const emailFromState = location?.state?.email || '';
  const emailFromRedux  = userData?.email || '';
  const emailFromQuery  = useMemo(() => {
    try { return new URLSearchParams(location.search).get('email') || ''; } catch { return ''; }
  }, [location.search]);
  const emailFromLS     = safeLS('fp_email');

  const email = (emailFromState || emailFromQuery || emailFromRedux || emailFromLS || '').trim();

  useEffect(() => {
    if (!email) {
      if (DEBUG) console.warn('[VerifyEmail] Missing email → redirecting back to /forget-password');
      setTimeout(() => navigate('/forget-password', { replace: true }), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const onResend = () => {
    if (!email || isLoading) return;
    setIsLoading(true);
    dispatch(forgetPasswordAuthService({ email }))
      .finally(() => setIsLoading(false));
  };

  const onSubmit = (data) => {
    if (!email) return;
    setIsLoading(true);

    dispatch(verifyTokenAuthService({ ...data, email }))
      .then((res) => {
        // ✅ axios shape: { data: { success, message, data: { _id, email, token } } }
        const token =
          res?.data?.data?.token ??
          res?.payload?.data?.token ?? // fallback if your apiService wraps it
          res?.payload?.token ??
          res?.data?.token ??
          res?.token ??
          null;

        if (!token) {
          console.error('[VerifyEmail] No token in response (expected data.data.token). Full resp:', res);
          throw new Error('Missing verification token from API');
        }

        safeSetLS('fp_email', email);
        safeSetLS('fp_token', token);

        navigate(
          `/reset-password?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`,
          { state: { email, token }, replace: true }
        );
      })
      .catch((err) => {
        const { message, errorData } = err || {};
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <section className="min-h-screen flex items-center" style={{ backgroundColor: BRAND.yellow }}>
      <div className="mx-auto max-w-7xl px-4 py-10 lg:py-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative z-10 lg:-mr-12">
              <div className="relative rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden" style={{ backgroundColor: BRAND.primary }}>
                <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: BRAND.yellow }} />
                <div className="p-6 md:p-10">
                  <div className="flex flex-col items-center mb-6">
                    <img src={logoImage} alt="Company logo" className="h-16 w-auto mb-3" />
                    <h1 className="text-3xl md:text-4xl font-semibold text-center tracking-tight text-white">Verify Email</h1>
                    <p className="mt-2 text-sm text-white/80 text-center">
                      Enter the 6-digit code we sent to <span className="font-semibold">{email}</span>
                    </p>
                  </div>

                  {Object.keys(errors || {}).length > 0 && (
                    <div className="mb-3 rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                      Please fix the highlighted fields below.
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <Controller
                      control={control}
                      name="token"
                      rules={{ required: true, minLength: 6, maxLength: 6 }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          autoFocus
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          labelText="Verification Code"
                          placeholder="Enter 6-digit code"
                          errorData={{ minLength: '6', maxLength: '6' }}
                          errorType={errors?.token?.type}
                          groupClassName="mb-1"
                        />
                      )}
                    />

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        disabled={isLoading}
                        className="font-medium hover:underline"
                        style={{ color: BRAND.yellow }}
                        onClick={onResend}
                      >
                        Resend code
                      </button>
                      <Link to="/forget-password" className="text-white/80 hover:underline">Change email</Link>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-xl font-semibold shadow focus:outline-none focus:ring-2 hover:brightness-95 active:brightness-90 disabled:opacity-80 disabled:cursor-not-allowed"
                      style={{ backgroundColor: BRAND.yellow, color: BRAND.ink, boxShadow: '0 12px 30px rgba(0,0,0,.25)' }}
                    >
                      {isLoading ? 'Verifying…' : 'Continue'}
                    </button>

                    <div className="pt-4 text-center text-[11px] text-white/80">Didn’t get it? Check spam folder</div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div
              className="relative h-[44vh] sm:h-[50vh] md:h-[58vh] lg:h-[78vh] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5"
              style={{ clipPath: 'polygon(7% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
            >
              <img src={backgroundImage} alt="Verification visuals" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-l from-black/15 via-transparent to-transparent" />
              <div className="absolute top-0 right-0 h-1 w-1/2" style={{ backgroundColor: BRAND.primary }} />
              <div className="hidden md:flex absolute bottom-4 right-4">
                <span className="px-4 py-2 rounded-full text-xs font-semibold shadow-md ring-1 ring-black/5 bg-white/90 text-gray-700 backdrop-blur">
                  Verify • 2-Step Security
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
